import { t } from "@/i18n-js/instance";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { UserImageStatus } from "@circle-react-shared/UserImageStatus";
import { BadgeV3 } from "@circle-react-shared/uikit/BadgeV3";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Dropdown } from "@circle-react-uikit/Dropdown";

interface ProfileActionItem {
  color?: string;
  icon: string;
  iconClassName?: string;
  iconSize?: number;
  label: string;
  onClick: (() => void) | (() => Promise<void>);
  value: string;
}

type ParticipantWithMessaging = ChatRoomParticipant & {
  messaging_enabled?: boolean;
};

interface ParticipantListProps {
  currentParticipant: ChatRoomParticipant;
  getProfileActionItems: (
    participant: ChatRoomParticipant,
  ) => ProfileActionItem[];
  participantList: ParticipantWithMessaging[];
  showMemberProfile: (memberPublicId: string) => void;
}

export const ParticipantList = ({
  currentParticipant,
  getProfileActionItems,
  participantList,
  showMemberProfile,
}: ParticipantListProps) => {
  const shouldShowDropdown = (participant: ChatRoomParticipant) => {
    const isCurrentUser = currentParticipant.id === participant.id;
    return !isCurrentUser && currentParticipant.admin;
  };

  return (
    <>
      {participantList.map(participant => {
        const isCurrentUser = currentParticipant.id === participant.id;
        const dropdownOptions = getProfileActionItems(participant);

        return (
          <TippyV2
            key={participant.id}
            content={t("messaging.member_disabled_messaging")}
            disabled={participant.messaging_enabled !== false}
          >
            <button
              type="button"
              className="hover:bg-tertiary messaging-rail__content--member focus-visible:bg-tertiary group w-full"
              aria-label={t("messaging.open_member_profile_button_aria_label", {
                name: participant.name,
              })}
              onClick={() =>
                showMemberProfile(participant.user_public_uid || "")
              }
            >
              <div
                className={classNames(
                  "text-dark flex w-[90%] items-center text-left",
                  { "opacity-50": participant.messaging_enabled === false },
                )}
              >
                <div className="mr-3">
                  <UserImageStatus
                    name={participant.name}
                    src={participant.avatar_url}
                    status={participant.status}
                    size="8"
                  />
                </div>
                <div className="truncate font-medium">
                  {isCurrentUser ? t("messaging.you") : participant.name}
                </div>
                {participant.admin && (
                  <div className="ml-3">
                    <BadgeV3 label={t("moderator")} />
                  </div>
                )}
              </div>
              {shouldShowDropdown(participant) && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  <Dropdown
                    direction="bottom-start"
                    menuButtonEl="div"
                    menuItemsClassName="!p-2"
                    button={
                      <Dropdown.MenuButton id="action-block">
                        <IconButton
                          name="20-menu-dots-horizontal"
                          iconSize={20}
                          ariaLabel={t("options_dropdown")}
                        />
                      </Dropdown.MenuButton>
                    }
                    options={dropdownOptions}
                  />
                </button>
              )}
            </button>
          </TippyV2>
        );
      })}
    </>
  );
};
